'use client';

import { type ChangeEvent, type MouseEvent, useCallback, useEffect, useId, useState } from 'react';

import { Dialog } from '@base-ui/react/dialog';

import { AddressAutocomplete } from '@/components/forms/address-autocomplete';
import {
  ADDRESS_INVALID_RESULT,
  type AddressComponents,
  type AddressSuggestion,
  type CasaResult,
  fetchHasMultipleUnits,
  gateAddressValidation,
  resolveAddressComponents,
  standardizeAddress,
} from '@/lib/address';
import {
  type SellerFlowChannel,
  type SellerRelationship,
  startSellerFlow,
} from '@/lib/seller-handoff';
import { opendoorUrl } from '@/lib/urls';

const RELATIONSHIP_OPTIONS: Array<{ value: SellerRelationship; label: string }> = [
  { value: 'Self', label: 'Yes, I own this home' },
  { value: 'Agent', label: 'No, I’m an agent' },
  { value: 'AgentAndOwner', label: 'I’m an agent, and own this home' },
  { value: 'Other', label: 'Other' },
];

function formatAddressLine(address: AddressComponents) {
  return `${address.city}, ${address.state} ${address.postalCode}`;
}

// Cosmos parity: cosmos's shared NewSellerFlowAddressInput reads `partner_referral`
// off the URL (useQueryParams) on every page that renders the address bar, so a
// partner referral deep-link (open.do/<partner>/<referrer> -> 307 to
// `<partner.LandingPage>?partner_referral=<id>`) keeps attribution wherever it
// lands. Read it here in the shared component (lazily, at submit/exit time) so
// every Beacon surface that renders this autocomplete behaves the same without
// per-page wiring. Client-only: no SSR/static-render impact.
function partnerReferralIdFromUrl(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  return new URLSearchParams(window.location.search).get('partner_referral') ?? undefined;
}

function manualAddressEntryAddress(address: AddressComponents) {
  return {
    ...(address.id ? { id: address.id } : {}),
    street1: address.street,
    city: address.city,
    state: address.state,
    postal_code: address.postalCode,
    unit: address.unit ?? '',
    ...(address.latitude !== undefined ? { latitude: address.latitude } : {}),
    ...(address.longitude !== undefined ? { longitude: address.longitude } : {}),
    ...(address.result ? { result: address.result } : {}),
    ...(address.source ? { source: address.source } : {}),
  };
}

// `address` is optional so the same helper handles the no-address exits
// (manual-entry CTA, unresolved selection, standardize transport failure)
// without dropping `trackingKeywords`/`channel`. Forgetting either on a
// partner-page exit silently re-routes the resulting lead to generic
// attribution because /track is never called in those branches and the
// downstream create-seller-input POST runs without the partner-attribution
// body field.
function manualAddressEntryUrl({
  address,
  trackingKeywords,
  channel,
  partnerReferralId,
}: {
  address?: AddressComponents | null;
  trackingKeywords?: string;
  channel?: SellerFlowChannel;
  partnerReferralId?: string;
}) {
  const params = new URLSearchParams();
  if (address) params.set('address', JSON.stringify(manualAddressEntryAddress(address)));
  if (trackingKeywords) params.set('trackingKeywords', trackingKeywords);
  if (channel) params.set('channel', channel);
  // Matches the cosmos /manual-address-entry query key so the fallback page
  // keeps partner-referral attribution.
  if (partnerReferralId) params.set('partnerReferralId', partnerReferralId);
  const qs = params.toString();
  return `${opendoorUrl('/manual-address-entry')}${qs ? `?${qs}` : ''}`;
}

function isRelationshipValue(value: string): value is SellerRelationship {
  return RELATIONSHIP_OPTIONS.some((option) => option.value === value);
}

// Exported separately so surfaces that already have a resolved address (e.g.
// the home-value estimator after a successful estimate) can reuse the same
// confirmation step without rendering another autocomplete input.
export function SellerFlowAddressConfirmation({
  address,
  open,
  onOpenChange,
  trackingKeywords,
  channel,
  needsUnitSelection = false,
}: {
  address: AddressComponents | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trackingKeywords?: string;
  channel?: SellerFlowChannel;
  // When true, the modal switches to "Is there a unit?" mode (cosmos parity):
  // the upstream Casa lookup flagged ADDRESS_MISSING_SUB_PREMISE, so the user
  // must either supply a unit or explicitly opt out via "No unit number"
  // before we submit. Mirrors cosmos AddressConfirmationModal so the
  // downstream lead has a coherent unit decision attached.
  needsUnitSelection?: boolean;
}) {
  const [confirming, setConfirming] = useState(false);
  const [relationship, setRelationship] = useState<SellerRelationship | ''>('');
  const [unit, setUnit] = useState('');
  const [noUnit, setNoUnit] = useState(false);
  // Inline error surfaced when standardizeAddress rejects a user-typed unit
  // (e.g. Casa returns ADDRESS_INVALID_SUB_PREMISE). Cleared on every unit
  // edit / noUnit toggle / modal close so the user can correct and retry.
  const [submitError, setSubmitError] = useState<string | null>(null);
  // useId so two modals on the same page (e.g. a sticky address widget +
  // home-value estimator on the same article) do not collide on a hardcoded
  // DOM id and break <label htmlFor>/screenreader association on the second.
  const relationshipId = useId();
  const unitId = useId();
  const noUnitId = useId();

  useEffect(() => {
    if (!open) {
      setConfirming(false);
      setRelationship('');
      setUnit('');
      setNoUnit(false);
      setSubmitError(null);
    }
  }, [open]);

  const handleEdit = useCallback(() => {
    if (!address) return;
    window.location.href = manualAddressEntryUrl({
      address,
      trackingKeywords,
      channel,
      partnerReferralId: partnerReferralIdFromUrl(),
    });
  }, [address, channel, trackingKeywords]);

  const handleRelationshipChange = useCallback((event: ChangeEvent<HTMLSelectElement>) => {
    const { value } = event.target;
    setRelationship(isRelationshipValue(value) ? value : '');
  }, []);

  const handleUnitChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setUnit(event.target.value);
    setSubmitError(null);
  }, []);

  const handleNoUnitToggle = useCallback(() => {
    setNoUnit((current) => {
      const next = !current;
      if (next) setUnit('');
      return next;
    });
    setSubmitError(null);
  }, []);

  // When the upstream flagged ADDRESS_MISSING_SUB_PREMISE the user must supply
  // a unit OR check "No unit number" before we'll submit. Mirrors cosmos's
  // AddressConfirmationModal disable predicate so we don't ship a half-resolved
  // address to Reception (which would create a generic-error lead).
  const unitGateBlocking = needsUnitSelection && !noUnit && unit.trim() === '';

  const handleConfirm = useCallback(async () => {
    if (!address || !relationship || confirming || unitGateBlocking) return;
    setConfirming(true);
    setSubmitError(null);
    const trimmedUnit = needsUnitSelection && !noUnit ? unit.trim() : '';
    const baseAddress: AddressComponents = needsUnitSelection
      ? { ...address, unit: trimmedUnit }
      : address;

    // Cosmos parity (cinderblocks AddressConfirmationModal): when the user
    // supplied a unit in response to an ADDRESS_MISSING_SUB_PREMISE flag, re-run
    // through Casa’s findByFields (our /_address-input/standardize endpoint) to
    // verify the unit exists. Skipped on the explicit “No unit number” branch
    // (Casa would just return MISSING_SUB_PREMISE again and we’d loop) and on
    // standardize transport failure (null — Athena re-validates server-side).
    let resolvedAddress: AddressComponents = baseAddress;
    if (needsUnitSelection && trimmedUnit) {
      const standardized = await standardizeAddress(baseAddress);
      if (standardized) {
        const codes: readonly CasaResult[] = standardized.result ?? [];
        const unitInvalid =
          codes.includes('ADDRESS_INVALID_SUB_PREMISE') ||
          codes.includes('ADDRESS_MISSING_SUB_PREMISE') ||
          codes.some((code) => ADDRESS_INVALID_RESULT.includes(code));
        if (unitInvalid) {
          setConfirming(false);
          setSubmitError(
            `We couldn’t find unit “${trimmedUnit}” at this address. Double-check the unit or select “No unit number.”`,
          );
          return;
        }
        // Use Casa's canonical version (it may normalize “4b” → “4B” etc.).
        resolvedAddress = standardized;
      }
    }

    void startSellerFlow(
      resolvedAddress,
      channel,
      relationship,
      trackingKeywords,
      partnerReferralIdFromUrl(),
    );
  }, [
    address,
    channel,
    confirming,
    needsUnitSelection,
    noUnit,
    relationship,
    trackingKeywords,
    unit,
    unitGateBlocking,
  ]);

  // Stable wrapper so the onClick prop doesn’t get a new function identity on
  // every render. handleConfirm is async; React onClick expects void, so we
  // discard the promise here.
  const handleConfirmClick = useCallback(() => {
    void handleConfirm();
  }, [handleConfirm]);

  // z-[110] sits above the sticky address widget stack: its backdrop is
  // z-[99] and the card itself is z-[100] (see
  // `components/article/sticky-address-widget.tsx`).
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Backdrop className="fixed inset-0 z-[110] bg-canvas-dark/70 transition-opacity data-[ending-style]:opacity-0 data-[starting-style]:opacity-0" />
        <Dialog.Popup className="fixed left-1/2 top-1/2 z-[110] max-h-[90vh] w-[calc(100%-32px)] max-w-[600px] -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-[2rem] bg-canvas p-6 shadow-2xl transition-all data-[ending-style]:scale-95 data-[ending-style]:opacity-0 data-[starting-style]:scale-95 data-[starting-style]:opacity-0 sm:p-10">
          <div className="flex items-start justify-between gap-6">
            <Dialog.Title className="font-medium leading-tight text-ink text-subheader-md sm:text-subheader-lg">
              {needsUnitSelection ? 'Is there a unit?' : 'Is this the correct home address?'}
            </Dialog.Title>
            <Dialog.Close
              aria-label="Close address confirmation"
              className="-mr-2 -mt-2 inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-ink transition hover:bg-state-hover"
            >
              <svg aria-hidden width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M6 6l12 12M18 6L6 18"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </Dialog.Close>
          </div>

          {address && (
            <div className="mt-8 rounded-3xl bg-canvas-muted p-6 sm:flex sm:items-center sm:justify-between sm:gap-6">
              <div className="text-ink text-body-lg sm:text-body-xl">
                <p>
                  {address.street}
                  {needsUnitSelection && unit.trim() && !noUnit ? `, #${unit.trim()}` : ''}
                </p>
                <p>{formatAddressLine(address)}</p>
              </div>
              <button
                type="button"
                onClick={handleEdit}
                className="mt-4 font-medium text-accent text-body-md hover:underline sm:mt-0"
              >
                Edit
              </button>
            </div>
          )}

          {needsUnitSelection && (
            <div className="mt-8">
              <label htmlFor={unitId} className="font-medium text-ink text-body-lg">
                Add unit number, apt, floor, etc. <span aria-hidden>*</span>
              </label>
              <input
                id={unitId}
                type="text"
                autoComplete="off"
                spellCheck={false}
                value={unit}
                onChange={handleUnitChange}
                disabled={noUnit}
                placeholder="#1234"
                className="mt-4 w-full rounded-2xl border border-line-strong bg-canvas px-5 py-4 text-ink text-body-md placeholder:text-ink-faint focus:outline-none focus:ring-2 focus:ring-accent disabled:bg-canvas-muted disabled:text-ink-faint"
              />
              <label
                htmlFor={noUnitId}
                className="mt-4 inline-flex cursor-pointer items-center gap-3 text-ink text-body-md"
              >
                <input
                  id={noUnitId}
                  type="checkbox"
                  checked={noUnit}
                  onChange={handleNoUnitToggle}
                  className="h-5 w-5 cursor-pointer rounded border-line-strong text-accent focus:ring-accent"
                />
                No unit number
              </label>
              {submitError && (
                <p role="alert" className="mt-3 text-error text-body-md">
                  {submitError}
                </p>
              )}
            </div>
          )}

          <div className="mt-8">
            <label htmlFor={relationshipId} className="font-medium text-ink text-body-lg">
              Are you the owner of this home? <span aria-hidden>*</span>
            </label>
            <div className="relative mt-4">
              <select
                id={relationshipId}
                aria-required
                value={relationship}
                onChange={handleRelationshipChange}
                className="w-full appearance-none rounded-2xl border border-line-strong bg-canvas px-5 py-4 pr-12 text-ink text-body-md focus:outline-none focus:ring-2 focus:ring-accent invalid:text-ink-faint"
              >
                <option value="">Select</option>
                {RELATIONSHIP_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <svg
                aria-hidden
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                className="pointer-events-none absolute right-5 top-1/2 -translate-y-1/2 text-ink"
              >
                <path
                  d="M4 7l6 6 6-6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>

          <button
            type="button"
            disabled={!relationship || confirming || unitGateBlocking}
            onClick={handleConfirmClick}
            className="mt-8 w-full rounded-2xl bg-accent px-6 py-4 font-medium text-canvas transition hover:bg-accent-hover disabled:cursor-not-allowed disabled:bg-accent-soft disabled:text-canvas/80 text-body-md"
          >
            {confirming ? 'Confirming…' : 'Confirm'}
          </button>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export function SellerFlowAddressAutocomplete({
  placeholder,
  loadingLabel = 'Confirming your address…',
  loadingClassName = 'text-ink-muted text-body-xl',
  trackingKeywords,
  channel,
}: {
  placeholder: string;
  loadingLabel?: string;
  loadingClassName?: string;
  trackingKeywords?: string;
  channel?: SellerFlowChannel;
}) {
  const [resolving, setResolving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState<AddressComponents | null>(null);
  const [needsUnitSelection, setNeedsUnitSelection] = useState(false);

  const navigateToManualEntry = useCallback(
    (address: AddressComponents | null) => {
      // Cosmos parity: when the address is unsupported, unresolved, or the
      // user picked the manual-entry CTA, route to /manual-address-entry
      // (not /address-entry) with tracking params preserved. Even without an
      // address payload, the URL must carry `trackingKeywords` and `channel`
      // so the downstream form submission keeps partner attribution —
      // dropping them here is the silent miss that re-routes a partner lead
      // to the generic Agent channel.
      window.location.href = manualAddressEntryUrl({
        address,
        trackingKeywords,
        channel,
        partnerReferralId: partnerReferralIdFromUrl(),
      });
    },
    [channel, trackingKeywords],
  );

  const handleSelect = useCallback(
    (suggestion: AddressSuggestion, sessionToken: string) => {
      if (resolving) return;
      setResolving(true);
      void (async () => {
        const resolved = await resolveAddressComponents(suggestion, sessionToken);
        if (!resolved) {
          navigateToManualEntry(null);
          return;
        }
        // Cosmos parity (cinderblocks addressValidation.ts validateAddress):
        // google-source predictions come back from /_address-input/place
        // without Casa result codes — they're whatever Google Places gave us.
        // Run them through /_address-input/standardize so Casa either upgrades
        // them to a canonical entry (with real result codes) or flags them as
        // ADDRESS_UNKNOWN, which gateAddressValidation will treat as
        // needsManualEntry below. Casa-source predictions skip this step
        // because /place already returned the canonical Casa shape.
        let components = resolved;
        if (resolved.source === 'google') {
          const standardized = await standardizeAddress(resolved);
          if (!standardized) {
            // Round-trip failed entirely; conservative posture is to push the
            // user into manual entry with whatever google fields we had,
            // matching cosmos' handling of a Casa outage.
            navigateToManualEntry(resolved);
            return;
          }
          components = standardized;
        }
        const { needsManualEntry, needsUnitSelection: nextNeedsUnitSelection } =
          gateAddressValidation(components.result);
        if (needsManualEntry) {
          navigateToManualEntry(components);
          return;
        }
        // Cosmos parity (cinderblocks addressValidation.ts): when Casa hasn't
        // already flagged ADDRESS_MISSING_SUB_PREMISE and the resolved address
        // has no unit, ask Casa whether the address actually has sibling
        // units. Buildings that Casa doesn't auto-flag still need the unit
        // prompt, and without this the user submits a unit-less building
        // address and Reception attaches the unit prompt only post-create.
        let shouldRequestUnit = nextNeedsUnitSelection;
        if (!shouldRequestUnit && components.id && !components.unit) {
          shouldRequestUnit = await fetchHasMultipleUnits(components.id);
        }
        setSelectedAddress(components);
        setNeedsUnitSelection(shouldRequestUnit);
        setModalOpen(true);
        setResolving(false);
      })();
    },
    [navigateToManualEntry, resolving],
  );

  const handleManualEntryClick = useCallback(() => {
    navigateToManualEntry(null);
  }, [navigateToManualEntry]);

  // Rendered as a footer item inside the autocomplete dropdown (see
  // AddressAutocomplete.belowAction). Plain link styling — the surrounding
  // <li> wrapper supplies padding and the separator border above when there
  // are suggestions to divide from.
  const manualEntryAction = (
    <button
      type="button"
      onMouseDown={preventManualEntryBlur}
      onClick={handleManualEntryClick}
      className="w-full cursor-pointer text-left font-medium text-accent text-body-md hover:underline"
    >
      Enter address manually instead
    </button>
  );

  return (
    <>
      <AddressAutocomplete
        placeholder={placeholder}
        onSelect={handleSelect}
        belowAction={manualEntryAction}
      />
      {resolving ? (
        <output className={`mt-3 block ${loadingClassName}`} aria-live="polite">
          {loadingLabel}
        </output>
      ) : null}
      <SellerFlowAddressConfirmation
        address={selectedAddress}
        open={modalOpen}
        onOpenChange={setModalOpen}
        trackingKeywords={trackingKeywords}
        channel={channel}
        needsUnitSelection={needsUnitSelection}
      />
    </>
  );
}

// Keep input focus while the click registers so the autocomplete dropdown
// doesn't collapse on mousedown (the dropdown is dismissed on input blur).
function preventManualEntryBlur(event: MouseEvent<HTMLButtonElement>) {
  event.preventDefault();
}
